import { Box } from "@mui/material";
import { Button, IconButton, THEME } from "@myloc/myloc-gui";
import { withPage } from "@myloc/myloc-utils";
import PropType from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Grid, List } from "../../../assets/icons";
import Search from "../../../assets/icons/Search";
import { useTranslate } from "../../../language/i18n";
import accountService from "../../../services/account/accountService";
import pages from "../../../utils/pages";
import { useMobile } from "../../../utils/viewport";
import { ORDER_CONTROL } from "../../BaseStorages/InventoryDetail/Components/productConstants";
import QRCodeButton from "../../shared/Button/QRCodeButton";
import Label from "../../shared/Label/Label";
import ScannerDialog from "../../shared/scanner/ScannerDialog";
import LoadingSpinner from "../../shared/Spinner/LoadingSpinner";
import ProductCard from "../ProductCard/ProductCard";
import { VIEWS } from "../ProductsPage";
import ProductCatalogueAddToCart from "./Components/ProductCatalogueAddToCart";
import styles from "./ProductCatalogue.module.scss";

const VIEW_SETTING_ID = "FSP_productCatalogueView";

const PARAMS = {
  VIEW: "view",
};
const ProductCatalogue = ({
  data,
  showFilter,
  productFilters,
  setProductFilters,
  setSearchValue,
  searchValue,
  isLoading,
  setParams,
  doSearch,
}) => {
  const translate = useTranslate();
  const isEmbedded = Boolean(window.location.pathname.match(/^\/embedded\//));
  const savedSettingValue = useRef();
  const isMobile = useMobile();
  const inputRef = useRef(null);

  const setActive = useCallback(
    async view => {
      const currentView = setParams(PARAMS.VIEW, view);

      if (currentView == null) return;
      setActiveView(currentView);
      if (savedSettingValue.current === currentView) return;
      const response = await accountService.setUserSetting(VIEW_SETTING_ID, { newValue: currentView });
      if (!response.isOk()) return;
      if (!response.data) return;
      savedSettingValue.current = response.data.value;
    },

    [setParams],
  );
  const [activeView, setActiveView] = useState();

  const clear = filterCategory => {
    let _filters = { ...productFilters };
    if (
      filterCategory === "includeNotOrderable" ||
      filterCategory === "isFixedAsset" ||
      filterCategory === "hasAvailableBalance"
    ) {
      _filters[filterCategory].include = false;
    }

    _filters[filterCategory].id = [];

    setProductFilters(_filters);
  };

  useEffect(() => {
    if (savedSettingValue.current != null) return;
    const getUserSetting = async () => {
      savedSettingValue.current = "";
      const response = await accountService.getUserSetting(VIEW_SETTING_ID, () => {});
      if (response.isOk()) {
        if (response.data) {
          savedSettingValue.current = response.data.value;
          setActive(response.data.value);
        }
      }
    };
    getUserSetting();
  }, [setActive]);

  const activeFilters = () => {
    if (!productFilters) {
      return [];
    }

    const filters = Object.keys(productFilters).filter(
      category => !!productFilters[category].id?.length || productFilters[category].include,
    );

    return filters.map(filterCategory => (
      <span className={styles.badge} key={filterCategory}>
        {translate(productFilters[filterCategory].title)} ({productFilters[filterCategory].id.length || "1"})
        <button type="button" onClick={() => clear(filterCategory)}>
          ✕
        </button>
      </span>
    ));
  };

  const [scannerOpen, setScannerOpen] = useState(false);
  const closeScanner = useCallback(() => {
    setScannerOpen(false);

    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const onScanQR = useCallback(
    result => {
      if (result[0]?.rawValue == null) return;

      closeScanner();
      setSearchValue(result[0].rawValue);
    },
    [closeScanner, setSearchValue],
  );

  return (
    <>
      <form
        onSubmit={e => {
          e.preventDefault();
          doSearch();
        }}
      >
        <div className={styles.row}>
          <input
            className={styles.input}
            placeholder={translate("SEARCH_FOR_PRODUCT_NUMBER_NAME_OR_SUPPLIER_ITEM_NUMBER")}
            value={searchValue}
            onChange={event => setSearchValue(event.target.value)}
            ref={inputRef}
          />
          <Box
            display={"flex"}
            flexDirection={"row"}
            gap={"1rem"}
            width={"100%"}
            alignItems={"center"}
            justifyContent={"space-between"}
          >
            <Box display={"flex"} flexDirection={"row"} gap={"1rem"} alignItems={"center"}>
              <QRCodeButton onClick={() => setScannerOpen(true)} />
              <Button type="submit">{isMobile ? <Search /> : translate("SEARCH")}</Button>
            </Box>
            <Button type="button" theme={THEME.SECONDARY} onClick={() => showFilter(true)}>
              {translate("FILTER")}
            </Button>
          </Box>
        </div>
      </form>
      <section className={styles.toolBar}>
        <div className={styles.activeFilters}>{activeFilters()}</div>
      </section>
      <div className={styles.views}>
        <IconButton onClick={() => setActive(VIEWS.GRID)} customCssClass={activeView == VIEWS.GRID && styles.active}>
          <Grid />
        </IconButton>
        <IconButton onClick={() => setActive(VIEWS.LIST)} customCssClass={activeView == VIEWS.LIST && styles.active}>
          <List size={42} />
        </IconButton>
      </div>
      {isLoading ? (
        <LoadingSpinner title="PRODUCTS_LOADING" />
      ) : activeView == VIEWS.GRID ? (
        <ul className={styles.productListGrid}>
          {!!data?.length &&
            data?.map(product => (
              <li key={product.id} className={styles.listItem}>
                <Link
                  to={{
                    pathname: isEmbedded
                      ? `${pages.EMBEDDED_PRODUCTS.PATH}/${product.id}`
                      : `${pages.PRODUCTS.PATH}/${product.id}`,
                    state: { product, referrer: `${window.location.pathname}${window.location.search}` },
                  }}
                  className={styles.productLink}
                >
                  <ProductCard product={product} />
                </Link>
              </li>
            ))}
        </ul>
      ) : (
        <>
          {!!data?.length &&
            data?.map(product => (
              <div key={product.id} className={styles.productList}>
                <div className={styles.upperRow}>
                  <p>{product.description}</p>
                  <Label
                    label={translate("AVAILABLE_BALANCE")}
                    value={`${product.defaultWarehouseBalance} ${product.unit}`}
                    inline
                  />
                </div>
                <div className={styles.lowerRow}>
                  <div className={styles.group}>
                    <Link
                      to={{
                        pathname: isEmbedded
                          ? `${pages.EMBEDDED_PRODUCTS.PATH}/${product.id}`
                          : `${pages.PRODUCTS.PATH}/${product.id}`,
                        state: { product, referrer: `${window.location.pathname}${window.location.search}` },
                      }}
                      className={styles.productLink}
                    >
                      <Label label={translate("PRODUCT_NUMBER_SHORT")} value={product.productNumber} inline />
                    </Link>
                    <p className={styles.greyText}>{`(${product.replenishmentGroup?.label})`}</p>
                  </div>
                  <Label label={translate("SUPPLIER_ITEM_NUMBER")} value={product.mainSupplierItemNumber} inline />
                  <Label label={translate("SUPPLIER")} value={product.supplier?.label} inline />
                  <p className={styles.greyText}>{`(${product.defaultWarehouseName})`}</p>
                </div>
                {product.orderControl != ORDER_CONTROL.INSTALLATION_MANDATORY.id && (
                  <ProductCatalogueAddToCart product={product} />
                )}
              </div>
            ))}
        </>
      )}
      <ScannerDialog open={scannerOpen} handleClose={closeScanner} onScan={onScanQR} />
    </>
  );
};

ProductCatalogue.propTypes = {
  productFilters: PropType.object,
  setProductFilters: PropType.func,
  doSearch: PropType.func.isRequired,
  showFilter: PropType.func,
  from: PropType.number,
  amount: PropType.number,
  count: PropType.number,
  search: PropType.string,
  searchValue: PropType.string,
  setSearchValue: PropType.func,
  data: PropType.arrayOf(
    PropType.shape({
      name: PropType.string,
      images: PropType.array,
      productNumber: PropType.string,
      quantityDecimals: PropType.number,
      quantity: PropType.number,
      id: PropType.string,
    }),
  ),
  isLoading: PropType.bool,
  setParams: PropType.func,
};

export default withPage(ProductCatalogue);
